import React from 'react';

/**
 * 通用页面布局组件
 * 
 * 使用 flex 布局分为三个区域：
 * - header: 顶部导航栏（可选）
 * - content: 可滚动的主要内容区域（flex-1）
 * - footer: 底部按钮区域（flex-shrink-0，始终可见）
 * 
 * 这样可以确保：
 * 1. 按钮始终在安全区域内，不会被挤出屏幕
 * 2. 内容区域可以滚动，适应不同屏幕高度
 * 3. 使用流式布局而不是固定 px/vh 定位
 */
interface PageLayoutProps {
  /** 顶部导航栏内容（可选） */
  header?: React.ReactNode;
  /** 主要内容区域 */
  children: React.ReactNode;
  /** 底部按钮区域（可选） */
  footer?: React.ReactNode;
  /** 背景样式（可选） */
  backgroundStyle?: React.CSSProperties;
  /** 额外的 className */
  className?: string;
  /** 内容区域 className（可选） */
  contentClassName?: string;
  /** 内容区域 style（可选） */
  contentStyle?: React.CSSProperties;
  /** 是否允许内容区域纵向滚动（默认 true） */
  scrollable?: boolean;
}

export const PageLayout: React.FC<PageLayoutProps> = ({
  header,
  children,
  footer,
  backgroundStyle,
  className = '',
  contentClassName = '',
  contentStyle,
  scrollable = true,
}) => {
  return (
    <div 
      className={`h-screen w-full flex flex-col overflow-hidden ${className}`}
      style={backgroundStyle}
    >
      {/* Header 区域：顶部导航栏 */}
      {header && (
        <div className="flex-shrink-0 relative z-10">
          {header}
        </div>
      )}

      {/* Content 区域：可滚动的主要内容 */}
      <div
        className={`flex-1 min-h-0 relative z-10 ${scrollable ? 'overflow-y-auto' : 'overflow-hidden'} ${contentClassName}`}
        style={contentStyle}
      >
        {children}
      </div>

      {/* Footer 区域：底部按钮，始终可见 */}
      {footer && (
        <div className="flex-shrink-0 relative z-20">
          {footer}
        </div>
      )}
    </div>
  );
};

